import React, { useEffect, useRef, useState } from 'react'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import {
  OldDropdown,
  Title,
  NavigationItem,
  ListItem,
  tokens,
  useThemeColors
} from '@arvoreeducacao/bonsai-ds'
import { useHistory, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { activitiesControllerGetPendingActivitiesCount } from 'api.gen'
import {
  ATUALIDADES_HOME_ROOT_PATH,
  EXPLORE_PATH,
  INGLES_HOME_ROOT_PATH,
  LIVROS_HOME_ROOT_PATH,
  LOGOUT_PATH,
  MY_ACCESS_PATH,
  MY_ACCOUNT_PATH,
  MY_NOTIFICATION_PREFERENCES_PATH,
  PAYMENT_PATH_QUERY_STRING,
  ROOT_PATH,
  SEARCH_PATH,
  SENTIR_HOME_ROOT_PATH,
  SENTIR_DIARIO_HOME_ROOT_PATH,
  COMUNICAR_HOME_ROOT_PATH,
  LER_HOME_ROOT_PATH,
  LER_REVISTAS_E_JORNAIS_HOME_ROOT_PATH,
  LER_NOTICIAS_HOME_ROOT_PATH,
  RECOMMENDATIONS_PATH,
  STORY_TREE_PATH,
  RUNNING_RECORDS_APP_V2_PATH,
  ACTIVITIES_V3_PATH,
  ADMIN_SUMMARY_REPORT_V2_PATH,
  ADMIN_SUMMARY_REPORT_V3_PATH,
  BIBLION_PANEL_PATH,
  BOOKFAIR_PATH
} from 'routes/paths'
import { hotjarEvent } from 'utils/ui-trackers/hotjar'
import { isImpersonating, stopImpersonating } from 'utils/impersonation'
import { useTranslation } from 'i18n'
import ClickOutSide from 'utils/ClickOutSide'
import {
  LanguageSwitcherItems,
  shouldRenderLanguageSwitcher
} from 'screens/site/language-switcher'
import { hideFreshChat, showFreshChat } from 'utils/fresh-chat/freshChat'
import { useSupportChat } from 'utils/support-chat'
import { getImageUrl } from 'utils/assets/Cdn'
// eslint-disable-next-line no-restricted-imports
import seedParanaLogo from 'images/logos/logo-seed-parana.svg'
// eslint-disable-next-line no-restricted-imports
import biblioSpLogo from 'images/logos/logo-bibliosp.svg'
import { useHeader } from './HeaderDesktop.hooks'

import {
  LogoArvore,
  Container,
  SearchWrapper,
  MenuWrapper,
  UserPill,
  UserPillAvatar,
  UserSubmenuWrapper,
  DropdownStyled,
  NavigationItemWrapper,
  NavigationItemContainer,
  SearchStyled,
  UserDropdownContainer,
  DropdownSearchStyled,
  OverlaySearch,
  NotificationStyledProfileWrapper,
  NotificationStyledProfile,
  NotificationStyled,
  ActivitiesBadge,
  HelpButtonLoading
} from './HeaderDesktop.styles'
import { canRenewFamilyPaying } from 'utils/family-paying'
import { post_event } from 'utils/analytics'
import { isTeacher } from 'utils/user/degree'
import type { MenuItemType } from './HeaderDesktop.types'
import {
  SECRETARIA_SP_HELP_PATH,
  RECIFE_HELP_PATH,
  BIBLION_HELP_PATH,
  SEED_PANEL_SECTION_PERMISSIONS,
  STUDENT_TUTORIAL_URL,
  TEACHER_TUTORIAL_URL,
  THEME_COLORS
} from './contants'
import SearchResultsList from 'screens/site/explore/search-results-list'
import SearchHistory from 'screens/site/explore/SearchHistory'
import { saveTermOnHistory } from 'screens/site/explore/SearchHistory/localStorageHistory'
import {
  shouldRenderMyAccount,
  shouldRenderSelectEntity,
  showRenderNotificationPreferences
} from 'utils/profile-menu/shouldRenderNotificationPreferences'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import useA11yEnabled from 'utils/hooks/use-a11y-enabled'
import { openAccessibilityMenu } from 'utils/user-way-accessibility-widget'
import SelectEntityItem from '../select-entity-item'
import SelectClassItem from '../select-class-item'
import SelectProfileItem from '../select-profile-item'
import {
  ADD_ACCOUNT_PATH,
  useProfileSwitch
} from '../select-profile-item/useProfileSwitch'
import { useSelectClassEligibility } from 'utils/hooks/use-select-class-eligibility'
import { SpecialEntities } from 'utils/user'
import ChatApp from 'components/chat-app'
import { isAdventista } from 'utils/user/acervo'
import FloraRouteTooltip from 'components/flora-route-tooltip/FloraRouteTooltip'
import NotificationBell from 'features/user-notifications/components/notification-bell'
import { redirectToAccountNextjs } from 'utils/account-nextjs'

const ADOPTION_LIST_MY_READINGS_URL = '/app-v2/my-readings'

const HeaderDesktop = () => {
  const { user } = useUserInfo()
  const canAccessStoryTree = user?.featureToggles?.storyTree
  const isAdmin = user?.isAdmin
  const { t } = useTranslation()
  const history = useHistory()
  const {
    activitiesTab,
    activitiesV3Tab,
    recommendationsTab,
    adminTab,
    panelTab,
    forestTab,
    homeTab,
    leagueTab,
    myReadingsTab,
    pressReaderTab,
    journeyTab,
    storyTreeComunicarTab,
    homeStoryTreeTab
  } = useHeader()

  const { searchTerm } = useParams<{ searchTerm: string }>()
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [searchValue, setSearchValue] = useState(searchTerm || '')
  const [isSearchResultsOpen, setIsSearchResultsOpen] = useState(false)
  const isOnExplorePage = history.location.pathname.includes(EXPLORE_PATH)
  const shouldRedirectToExplore =
    (user?.canAccessCategories || isAdventista(user)) && !isOnExplorePage
  const {
    isPendingOpen: isSupportChatPending,
    handleClick: handleSupportChatClick,
    cancelPendingOpen: cancelSupportChatPending
  } = useSupportChat(user)
  const canDoEarlyRenewal = canRenewFamilyPaying(user)
  const isBookfairContract =
    user?.reader?.entity?.contractInfo?.type === 'fp_bookfair'
  const renderLanguageSwitcher = shouldRenderLanguageSwitcher(user)
  const inputSearchRef = useRef(null)
  const userMenuTriggerRef = useRef<HTMLDivElement>(null)
  const TUTORIAL_PATH =
    isTeacher(user) || user.teacher
      ? TEACHER_TUTORIAL_URL
      : STUDENT_TUTORIAL_URL

  const { setColorPallete, colorPaletteName } = useThemeColors()

  searchValue.length > 1
    ? hideFreshChat()
    : showFreshChat(user?.reader?.root?.id)

  const { featureToggles } = useFeatureToggles()

  const { shouldRenderSelectClass, currentEntityName: selectClassEntityName } =
    useSelectClassEligibility()
  const { shouldRenderProfileSwitch, canAddAccount } = useProfileSwitch()
  const userNameParts = (user?.name ?? '').trim().split(/\s+/).filter(Boolean)
  const userInitials = `${userNameParts[0]?.[0] ?? ''}${
    userNameParts.length > 1 ? userNameParts[userNameParts.length - 1][0] : ''
  }`.toUpperCase()
  const profileSwitchEntityName = shouldRenderSelectClass
    ? selectClassEntityName ?? user?.reader?.entity?.name
    : undefined
  const LAYOUT_THEME = featureToggles?.layoutThema
  const hasBiblionGeneral = featureToggles?.biblionGeneral
  const hasSeedPrPoc = featureToggles?.seedPrPoc === true
  const hasSeedPanelSectionPermission = SEED_PANEL_SECTION_PERMISSIONS.some(
    permission => user?.permissions?.includes(permission)
  )
  const hasSeedParanaGeneral = featureToggles?.seedParanaGeneral
  const hasBiblioSpGeneral = featureToggles?.biblioSpGeneral
  const { a11yEnabled } = useA11yEnabled()
  const activeItemColor = a11yEnabled ? tokens.colors.primary900 : undefined
  const showBeta = useFeatureFlagEnabled('beta-testers')

  const shouldFetchPendingCount = Boolean(
    user.featureToggles?.activitiesV3 && user.canAccessActivities
  )

  const { data: pendingActivitiesData } = useQuery({
    queryKey: ['pending-activities-count'],
    queryFn: () => activitiesControllerGetPendingActivitiesCount(),
    enabled: shouldFetchPendingCount
  })

  const activitiesNotificationCount = pendingActivitiesData?.data?.count ?? 0

  useEffect(() => {
    if (!showUserMenu) return

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return

      setShowUserMenu(false)
      cancelSupportChatPending()
      userMenuTriggerRef.current?.querySelector<HTMLElement>('button')?.focus()
    }

    document.addEventListener('keydown', closeOnEscape)

    return () => document.removeEventListener('keydown', closeOnEscape)
  }, [showUserMenu, cancelSupportChatPending])

  const toggleTheme = () => {
    colorPaletteName === THEME_COLORS.LIGHT
      ? setColorPallete(THEME_COLORS.DARK)
      : setColorPallete(THEME_COLORS.LIGHT)
  }

  const navigateToAccount = (legacyPath: string) => {
    setShowUserMenu(false)
    if (featureToggles?.myAccountNextjs) {
      redirectToAccountNextjs(legacyPath)
      return
    }
    history.push(legacyPath)
  }

  const navItemRender = (items: MenuItemType[]) => {
    const currentPath = history.location.pathname
    const isCompact = items.length >= 6
    let activePage: number

    const homesPaths = [
      LIVROS_HOME_ROOT_PATH,
      LER_HOME_ROOT_PATH,
      LER_REVISTAS_E_JORNAIS_HOME_ROOT_PATH,
      LER_NOTICIAS_HOME_ROOT_PATH,
      ATUALIDADES_HOME_ROOT_PATH,
      INGLES_HOME_ROOT_PATH,
      COMUNICAR_HOME_ROOT_PATH,
      SENTIR_HOME_ROOT_PATH,
      SENTIR_DIARIO_HOME_ROOT_PATH,
      ROOT_PATH,
      STORY_TREE_PATH
    ]

    if (homesPaths.includes(currentPath)) {
      activePage = 0
    } else {
      activePage = items.find(item => {
        return (
          !homesPaths.includes(item.pathToGo) &&
          currentPath.includes(item.pathToGo)
        )
      })?.id
    }

    return (
      <MenuWrapper
        as="nav"
        id="header-desktop"
        aria-label={t('Navegação principal')}
      >
        <ChatApp user={user} />
        <div>
          <LogoArvore
            height={30}
            width={140}
            src={
              hasSeedParanaGeneral
                ? seedParanaLogo
                : hasBiblioSpGeneral
                ? biblioSpLogo
                : hasBiblionGeneral
                ? 'https://cdn.arvore.com.br/files.arvoredelivros.com.br/arvore-library-assets/images/biblion/logo-biblion.svg'
                : getImageUrl(
                    canAccessStoryTree
                      ? 'story-tree/story-tree-logo.svg'
                      : 'logos/logo-arvore.svg'
                  )
            }
            alt={
              hasSeedParanaGeneral
                ? t('Logo da SEED Paraná')
                : hasBiblioSpGeneral
                ? t('Logo da BiblioSP Digital')
                : hasBiblionGeneral
                ? t('Logo do Biblion')
                : canAccessStoryTree
                ? t('Logo do Story Tree')
                : t('Logo da Árvore')
            }
            onClick={() =>
              history.push(canAccessStoryTree ? STORY_TREE_PATH : ROOT_PATH)
            }
          />
        </div>
        <div>
          {items.map(item => {
            const isActivitiesV3 = item.pathToGo === ACTIVITIES_V3_PATH
            const shouldShowBadge =
              isActivitiesV3 && activitiesNotificationCount > 0

            return (
              <NavigationItemWrapper
                key={item.label}
                id={item.label}
                hasBadge={shouldShowBadge}
                compact={isCompact}
              >
                <NavigationItemContainer>
                  <NavigationItem
                    text={item.label}
                    ariaLabel={item.label}
                    active={activePage === item.id}
                    activeColor={activeItemColor}
                    leftIconName={
                      activePage === item.id
                        ? item.iconNameActive
                        : item.iconName
                    }
                    onClick={() => {
                      user.isFreemium && hotjarEvent(item.hotjarEventName)

                      if (item.pathToGo === myReadingsTab.pathToGo) {
                        window.location.href = ADOPTION_LIST_MY_READINGS_URL
                        return
                      }

                      if (
                        user.featureToggles?.doActivityV2 &&
                        item.pathToGo === RECOMMENDATIONS_PATH
                      ) {
                        window.location.href = `${window.location.origin}${RECOMMENDATIONS_PATH}`
                        return
                      }

                      if (item.pathToGo === RUNNING_RECORDS_APP_V2_PATH) {
                        window.location.href = `${window.location.origin}${RUNNING_RECORDS_APP_V2_PATH}`
                        return
                      }

                      if (item.pathToGo === ADMIN_SUMMARY_REPORT_V2_PATH) {
                        window.location.href = `${window.location.origin}${ADMIN_SUMMARY_REPORT_V2_PATH}`
                        return
                      }

                      if (item.pathToGo === ADMIN_SUMMARY_REPORT_V3_PATH) {
                        window.location.href = `${window.location.origin}${ADMIN_SUMMARY_REPORT_V3_PATH}`
                        return
                      }

                      if (item.pathToGo === BIBLION_PANEL_PATH) {
                        window.location.href = `${window.location.origin}${BIBLION_PANEL_PATH}`
                        return
                      }

                      if (item.pathToGo.includes('http')) {
                        window.location.href = item.pathToGo
                        return
                      }

                      if (item.pathToGo === ACTIVITIES_V3_PATH) {
                        window.location.href = `${window.location.origin}${ACTIVITIES_V3_PATH}`
                        return
                      }

                      if (item.pathToGo.startsWith('/app-v2')) {
                        window.location.href = `${window.location.origin}${item.pathToGo}`
                        return
                      }

                      history.push(item.pathToGo)
                    }}
                  />
                  {shouldShowBadge && (
                    <ActivitiesBadge a11yEnabled={a11yEnabled}>
                      {activitiesNotificationCount}
                    </ActivitiesBadge>
                  )}
                </NavigationItemContainer>
              </NavigationItemWrapper>
            )
          })}
          {user.featureToggles?.ottoAiEnabled && user.teacher?.id !== null && (
            <FloraRouteTooltip>
              <img
                src={getImageUrl('logos/flora-ai-logo.svg')}
                alt="Flora AI"
                className="w-full h-full object-contain relative z-10"
              />
            </FloraRouteTooltip>
          )}
          <SearchWrapper
            data-testid="search-input-header-wrapper"
            onClick={() => {
              if (shouldRedirectToExplore && searchValue.length === 0) {
                history.push(EXPLORE_PATH)
                return
              }
              setIsSearchResultsOpen(true)
            }}
          >
            <SearchStyled
              ref={inputSearchRef}
              data-testid="search-input-header"
              aria-label={t('Pesquisar')}
              mode="primary"
              onClick={() => {
                if (shouldRedirectToExplore && searchValue.length === 0) {
                  return
                }
                setIsSearchResultsOpen(true)
              }}
              placeholder={
                hasBiblionGeneral
                  ? t('Pesquise na BibliON')
                  : canAccessStoryTree
                  ? t('Pesquise em StoryTree')
                  : t('Pesquise na Árvore')
              }
              onChange={value => {
                setSearchValue(value.target.value)
                setIsSearchResultsOpen(true)
                if (
                  shouldRedirectToExplore &&
                  value.target.value.length === 0
                ) {
                  history.push(EXPLORE_PATH)
                }
              }}
              onSubmit={() => {
                if (!searchValue.length) return
                user.isFreemium && hotjarEvent('busca')
                saveTermOnHistory(searchValue)
                history.push(SEARCH_PATH(encodeURIComponent(searchValue)))
                setIsSearchResultsOpen(false)
              }}
              value={searchValue}
            />
          </SearchWrapper>
          {isSearchResultsOpen && (
            <div>
              <OverlaySearch
                showOverlay
                onClick={() => setIsSearchResultsOpen(false)}
              />
              <DropdownSearchStyled>
                {searchValue.length >= 1 ? (
                  <SearchResultsList
                    inputValue={searchValue}
                    onSelectBook={() => setIsSearchResultsOpen(false)}
                    handleSubmit={() => {
                      saveTermOnHistory(searchValue)
                      user.isFreemium && hotjarEvent('busca')
                      history.push(SEARCH_PATH(encodeURIComponent(searchValue)))
                      setIsSearchResultsOpen(false)
                    }}
                  />
                ) : (
                  <SearchHistory
                    compact
                    handleSubmit={term => {
                      saveTermOnHistory(term)
                      user.isFreemium && hotjarEvent('busca')
                      history.push(SEARCH_PATH(encodeURIComponent(term)))
                      setIsSearchResultsOpen(false)
                    }}
                  />
                )}
              </DropdownSearchStyled>
            </div>
          )}
          {featureToggles?.customNotifications && !hasBiblionGeneral && (
            <NotificationBell />
          )}
          <UserSubmenuWrapper>
            <NotificationStyledProfileWrapper ref={userMenuTriggerRef}>
              <UserPill
                type="button"
                $open={showUserMenu}
                onClick={() => {
                  setShowUserMenu(!showUserMenu)
                }}
                data-testid="user-name-button"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
              >
                <UserPillAvatar aria-hidden="true">{userInitials}</UserPillAvatar>
                {user?.name?.split(' ')[0]}
              </UserPill>
              {canDoEarlyRenewal && (
                <NotificationStyledProfile theme={{ type: 'bullet' }} />
              )}
            </NotificationStyledProfileWrapper>
            {showUserMenu && (
              <DropdownStyled data-testid="submenu-box">
                <ClickOutSide
                  onClickOutSide={() => {
                    setShowUserMenu(false)
                    cancelSupportChatPending()
                  }}
                >
                  {isImpersonating() && (
                    <ListItem
                      icon="eye-off"
                      text={t('Despersonificar')}
                      color={tokens.colors.red3Dark}
                      onClick={() => stopImpersonating()}
                    />
                  )}
                  <SelectProfileItem entityName={profileSwitchEntityName} />
                  {shouldRenderSelectEntity(user) && (
                    <SelectEntityItem entityName={user?.reader?.entity?.name} />
                  )}
                  {shouldRenderSelectClass && !shouldRenderProfileSwitch && (
                    <SelectClassItem
                      entityName={
                        selectClassEntityName ?? user?.reader?.entity?.name
                      }
                    />
                  )}
                  {shouldRenderMyAccount(user) && (
                    <ListItem
                      icon="user"
                      text={t('Minha conta')}
                      onClick={() => navigateToAccount(MY_ACCOUNT_PATH)}
                    />
                  )}

                  <ListItem
                    icon="lock"
                    text={t('Meu acesso')}
                    onClick={() => {
                      user.isFreemium && hotjarEvent('menu_meu_acesso')
                      navigateToAccount(MY_ACCESS_PATH)
                    }}
                  />

                  {showRenderNotificationPreferences(user) &&
                    !hasBiblionGeneral && (
                      <ListItem
                        icon="bell"
                        text={t('Avisos')}
                        onClick={() =>
                          navigateToAccount(MY_NOTIFICATION_PREFERENCES_PATH)
                        }
                      />
                    )}

                  {renderLanguageSwitcher && (
                    <ListItem
                      icon="globe"
                      text={t('Idioma')}
                      onClick={() => {
                        setIsLanguageSelectorOpen(true)
                        setShowUserMenu(false)
                      }}
                    />
                  )}
                  <ListItem
                    icon="universal-access"
                    text={t('Acessibilidade')}
                    onClick={() => {
                      openAccessibilityMenu(featureToggles?.vLibrasEnabled),
                        setShowUserMenu(false)
                    }}
                  />
                  {showBeta && (
                    <ListItem
                      icon="science"
                      text={t('Novidades em teste')}
                      onClick={() => {
                        setShowUserMenu(false)
                        window.location.href = '/app-v2/early-access'
                      }}
                    />
                  )}
                  {canDoEarlyRenewal && (
                    <div>
                      <ListItem
                        icon="crdit-card-alt"
                        text={t('Renovar assinatura')}
                        onClick={() => {
                          user.isFreemium &&
                            hotjarEvent('early-renew-menu-item')
                          post_event(
                            'UserMenuOptionClicked',
                            'early-renew-menu-item'
                          )
                          setShowUserMenu(false)
                          history.push(
                            isBookfairContract
                              ? BOOKFAIR_PATH
                              : PAYMENT_PATH_QUERY_STRING('menu')
                          )
                        }}
                      />
                      <NotificationStyled theme={{ type: 'bullet' }} />
                    </div>
                  )}
                  {!hasBiblionGeneral &&
                    (isSupportChatPending ? (
                      <HelpButtonLoading>
                        <div className="spinner" />
                        <span>{t('Precisa de ajuda?')}</span>
                      </HelpButtonLoading>
                    ) : (
                      <ListItem
                        icon="headset"
                        text={t('Precisa de ajuda?')}
                        onClick={handleSupportChatClick}
                      />
                    ))}
                  <ListItem
                    icon="information"
                    text={
                      hasBiblionGeneral
                        ? t('Preciso de ajuda')
                        : t('Central Árvore')
                    }
                    onClick={() => {
                      if (hasBiblionGeneral) {
                        window.location.href = BIBLION_HELP_PATH
                      } else if (SpecialEntities.isSecretariaSp(user)) {
                        window.location.href = SECRETARIA_SP_HELP_PATH
                      } else if (SpecialEntities.isRecife(user)) {
                        window.location.href = RECIFE_HELP_PATH
                      } else {
                        window.location.href = TUTORIAL_PATH
                      }
                    }}
                  />
                  {isAdmin && (
                    <ListItem
                      icon="settings"
                      text={t('Área de Administração')}
                      onClick={() =>
                        (window.location.href = '/app-v2/backoffice-v2')
                      }
                    />
                  )}

                  {LAYOUT_THEME && colorPaletteName === THEME_COLORS.DARK && (
                    <ListItem
                      icon="sun_3_"
                      text={t('Tema Claro')}
                      onClick={() => toggleTheme()}
                    />
                  )}

                  {LAYOUT_THEME && colorPaletteName === THEME_COLORS.LIGHT && (
                    <ListItem
                      icon="moon"
                      text={t('Tema Escuro')}
                      onClick={() => toggleTheme()}
                    />
                  )}

                  {canAddAccount && (
                    <ListItem
                      icon="user-add"
                      text={t('Adicionar conta')}
                      onClick={() => (window.location.href = ADD_ACCOUNT_PATH)}
                    />
                  )}
                  <ListItem
                    icon="send-out"
                    text={t('Sair dessa conta')}
                    color={tokens.colors.red3Dark}
                    onClick={() => history.push(LOGOUT_PATH)}
                  />
                </ClickOutSide>
              </DropdownStyled>
            )}
          </UserSubmenuWrapper>
        </div>
      </MenuWrapper>
    )
  }

  const renderNavItens = () => {
    const showPressreaderIconOnHeader =
      user.featureToggles?.showPressreaderIconOnHeader

    const menuItems = []

    canAccessStoryTree
      ? menuItems.push(homeStoryTreeTab)
      : menuItems.push(homeTab)

    Boolean(canAccessStoryTree && !user?.teacher) && menuItems.push(journeyTab)

    canAccessStoryTree &&
      !user?.teacher &&
      menuItems.push(storyTreeComunicarTab)
    menuItems.push(myReadingsTab)
    !user.canAccessForest &&
      user.canAccessLeagueContent &&
      menuItems.push(leagueTab)
    user.canAccessForest && menuItems.push(forestTab)

    if (user.canAccessActivities) {
      if (user.featureToggles?.activitiesV3) {
        menuItems.push(activitiesV3Tab)
      } else if (user.featureToggles?.doActivityV2) {
        menuItems.push(recommendationsTab)
      } else {
        menuItems.push(activitiesTab)
      }
    }

    user.canAccessAdminPanel &&
      !hasBiblionGeneral &&
      !hasSeedPrPoc &&
      menuItems.push(adminTab)
    Boolean(
      featureToggles?.biblionPanel ||
        (hasSeedPrPoc && hasSeedPanelSectionPermission)
    ) &&
      Boolean(user.teacher?.id) &&
      menuItems.push(panelTab)
    showPressreaderIconOnHeader && menuItems.push(pressReaderTab)

    return navItemRender(menuItems)
  }

  return (
    <>
      {isLanguageSelectorOpen && (
        <ClickOutSide
          onClickOutSide={() => {
            setIsLanguageSelectorOpen(false)
          }}
        >
          <UserDropdownContainer>
            <OldDropdown>
              <div
                style={{
                  paddingTop: 12,
                  paddingLeft: 12,
                  paddingBottom: 12
                }}
              >
                <Title size={4}>{t('Idioma')}</Title>
              </div>
              <LanguageSwitcherItems />
            </OldDropdown>
          </UserDropdownContainer>
        </ClickOutSide>
      )}

      <Container data-test="appnav" data-testid="header-desktop">
        {renderNavItens()}
      </Container>
    </>
  )
}

export default HeaderDesktop
